'use client';

import { useState, useEffect, useRef, startTransition } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { useCart } from '@/context/CartContext';
import { Product, ProductReview } from '@/types';
import ProductGrid from '@/components/ProductGrid';
import Breadcrumbs from '@/components/Breadcrumbs';
import ReviewsCarousel from '@/components/ReviewsCarousel';
import {
  getProductAnalytics,
  trackAddToCartClicked,
  trackEvent,
  trackProductViewed,
} from '@/lib/posthog';
import {
  IoStar,
  IoCart,
  IoSparkles,
  IoShieldCheckmarkOutline,
  IoHelpCircleOutline,
  IoCheckmarkCircle,
  IoLockClosedOutline,
  IoArrowForward,
  IoTimeOutline,
  IoInformationCircleOutline,
  IoEyeOutline,
  IoAdd,
  IoRemove,
} from 'react-icons/io5';

interface AghoriLocketClientProps {
  product: Product;
  reviews: ProductReview[];
  relatedProducts: Product[];
  faqs: Array<{ question: string; answer: string }>;
}

export default function AghoriLocketClient({
  product,
  reviews,
  relatedProducts,
  faqs,
}: AghoriLocketClientProps) {
  const router = useRouter();
  const { addToCart, cartItems } = useCart();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isStickyVisible, setIsStickyVisible] = useState(false);
  const buyButtonRef = useRef<HTMLButtonElement>(null);

  const images: string[] = Array.isArray(product.image_url)
    ? product.image_url
    : product.image_url
      ? [product.image_url]
      : [];

  const quantityInCart = cartItems.find((item) => item.id === product.id)?.quantity || 0;

  useEffect(() => {
    trackProductViewed(product, {
      from_page: 'aghori_locket_page',
    });
  }, [product]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStickyVisible(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    const target = buyButtonRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, []);

  const handleAddToCart = () => {
    trackAddToCartClicked(product, {
      from_page: 'aghori_locket_page',
      element_section: 'product_hero',
      quantity,
      cart_status_before: quantityInCart,
    });
    startTransition(() => {
      for (let i = 0; i < quantity; i++) {
        addToCart(product);
      }
    });
  };

  const handleBuyNow = () => {
    trackEvent(
      'buy_now_clicked',
      getProductAnalytics(product, {
        from_page: 'aghori_locket_page',
        cart_status_before: quantityInCart,
      })
    );
    if (quantityInCart === 0) {
      trackAddToCartClicked(product, {
        from_page: 'aghori_locket_page',
        element_section: 'buy_now',
        quantity,
        cart_status_before: quantityInCart,
      });
      startTransition(() => {
        for (let i = 0; i < quantity; i++) {
          addToCart(product);
        }
      });
    }
    router.push('/cart');
  };

  const breadcrumbItems = [
    { label: 'Evil Eye Protection', href: '/products?byTag=evil-eye' },
    { label: 'Aghori Evil Eye Locket' },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF9] pb-24 text-slate-800">
      {/* 1. TOP BREADCRUMBS & HERO SHOWCASE */}
      <section className="border-b border-orange-100 bg-gradient-to-b from-[#FFF7E8] to-[#FFFDF9] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Breadcrumbs items={breadcrumbItems} className="mb-4" />

          <div className="grid gap-8 lg:grid-cols-12 lg:items-start">
            {/* LEFT: PRODUCT IMAGE GALLERY (5 cols) */}
            <div className="lg:col-span-6 lg:sticky lg:top-24">
              <div className="overflow-hidden rounded-3xl border border-orange-100 bg-white p-4 shadow-sm">
                <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-stone-100">
                  {images[selectedImageIndex] ? (
                    <Image
                      src={images[selectedImageIndex]}
                      alt={`${product.name} - Image ${selectedImageIndex + 1}`}
                      fill
                      priority
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      className="object-contain transition-transform duration-300 hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-stone-400">
                      <IoEyeOutline size={48} />
                    </div>
                  )}
                  <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow">
                    <IoSparkles size={12} /> Abhimantrit
                  </div>
                </div>

                {/* THUMBNAILS */}
                {images.length > 1 && (
                  <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                    {images.map((img, idx) => (
                      <button
                        key={img}
                        type="button"
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl border-2 transition-all ${
                          selectedImageIndex === idx
                            ? 'border-amber-600 ring-2 ring-amber-400/30'
                            : 'border-orange-100 hover:border-amber-300'
                        }`}
                      >
                        <Image
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          fill
                          sizes="80px"
                          className="object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* TRUST PILLS */}
                <div className="mt-5 grid grid-cols-2 gap-2 border-t border-orange-100 pt-4 text-xs font-semibold text-stone-700 sm:grid-cols-4">
                  <div className="flex items-center gap-1.5 rounded-lg bg-orange-50/70 p-2">
                    <IoCheckmarkCircle className="text-amber-700" size={16} /> Original Hakik
                  </div>
                  <div className="flex items-center gap-1.5 rounded-lg bg-orange-50/70 p-2">
                    <IoCheckmarkCircle className="text-amber-700" size={16} /> German Silver
                  </div>
                  <div className="flex items-center gap-1.5 rounded-lg bg-orange-50/70 p-2">
                    <IoCheckmarkCircle className="text-amber-700" size={16} /> Saturday Wear
                  </div>
                  <div className="flex items-center gap-1.5 rounded-lg bg-orange-50/70 p-2">
                    <IoCheckmarkCircle className="text-amber-700" size={16} /> Ready to Wear
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: PRODUCT INFO & PURCHASE PATH (6 cols) */}
            <div className="lg:col-span-6 space-y-6">
              <div className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm sm:p-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary mb-3">
                  <IoShieldCheckmarkOutline /> Authentic Protection Talisman
                </div>

                <h1 className="font-serif text-2xl font-bold leading-tight text-accent sm:text-4xl">
                  Aghori Evil Eye Locket (Original Hakik Stone &amp; German Silver)
                </h1>

                {/* RATING SNIPPET */}
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }, (_, i) => (
                      <IoStar
                        key={i}
                        className={i < Math.round(product.rating || 4.45) ? 'text-amber-500' : 'text-stone-300'}
                        size={16}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-bold text-stone-900">
                    {product.rating || 4.45} / 5.0
                  </span>
                  <span className="text-xs text-stone-400">|</span>
                  <a
                    href="#customer-reviews"
                    className="text-xs font-semibold text-primary underline underline-offset-4 hover:text-amber-700"
                  >
                    {product.review_count || 11} Verified Customer Reviews
                  </a>
                </div>

                {/* PRICING */}
                <div className="mt-5 flex items-baseline gap-3">
                  <span className="font-serif text-3xl font-bold text-accent sm:text-4xl">
                    ₹{product.price}
                  </span>
                  {product.compare_at_price && product.compare_at_price > product.price && (
                    <>
                      <span className="text-lg text-stone-400 line-through">
                        ₹{product.compare_at_price}
                      </span>
                      <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">
                        Save ₹{product.compare_at_price - product.price}
                      </span>
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-emerald-700 font-semibold">
                  ✓ Free Express Shipping Across India • Cash on Delivery Available
                </p>

                {/* SHORT SUMMARY */}
                <p className="mt-4 text-sm leading-relaxed text-text-secondary sm:text-base">
                  Handcrafted using a genuine black-and-white Evil Eye Hakik (natural Agate) stone encased in protective German Silver. Consecrated for personal spiritual wear, grounding, and traditional protection against envious vibrations.
                </p>

                {/* SPECS GRID */}
                <div className="mt-5 grid grid-cols-2 gap-2.5 rounded-2xl border border-orange-100 bg-[#FFFDF9] p-4 text-xs">
                  <div>
                    <span className="font-bold uppercase tracking-wider text-stone-400">Stone</span>
                    <p className="font-semibold text-accent mt-0.5">Original Evil Eye Hakik (Agate)</p>
                  </div>
                  <div>
                    <span className="font-bold uppercase tracking-wider text-stone-400">Casing</span>
                    <p className="font-semibold text-accent mt-0.5">German Silver (Normal fit)</p>
                  </div>
                  <div>
                    <span className="font-bold uppercase tracking-wider text-stone-400">Best Day</span>
                    <p className="font-semibold text-accent mt-0.5">Saturday (Any time)</p>
                  </div>
                  <div>
                    <span className="font-bold uppercase tracking-wider text-stone-400">Wear With</span>
                    <p className="font-semibold text-accent mt-0.5">Black thread or silver chain</p>
                  </div>
                </div>

                {/* QUANTITY & ACTIONS */}
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-stone-600">
                      Quantity:
                    </span>
                    <div className="flex items-center rounded-xl border border-orange-200 bg-white">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="p-2 text-stone-600 hover:text-amber-700 transition"
                        aria-label="Decrease quantity"
                      >
                        <IoRemove size={16} />
                      </button>
                      <span className="w-10 text-center text-sm font-bold text-accent">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => q + 1)}
                        className="p-2 text-stone-600 hover:text-amber-700 transition"
                        aria-label="Increase quantity"
                      >
                        <IoAdd size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border-2 border-accent bg-white px-6 font-bold text-accent transition hover:bg-orange-50 active:scale-[0.98]"
                    >
                      <IoCart size={18} /> Add to Cart
                    </button>
                    <button
                      ref={buyButtonRef}
                      type="button"
                      onClick={handleBuyNow}
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-accent to-stone-900 px-6 font-bold text-white shadow-lg transition hover:brightness-110 active:scale-[0.98]"
                    >
                      Buy Now <IoArrowForward size={18} />
                    </button>
                  </div>
                </div>

                {/* REASSURANCE */}
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-orange-100 pt-4 text-xs text-stone-500">
                  <span className="flex items-center gap-1">
                    <IoShieldCheckmarkOutline className="text-emerald-600" /> 7-Day Easy Returns
                  </span>
                  <span className="flex items-center gap-1">
                    <IoLockClosedOutline className="text-amber-600" /> Safe &amp; Secure Checkout
                  </span>
                  <span className="flex items-center gap-1">
                    <IoCheckmarkCircle className="text-primary" /> Consecrated by AstroJi
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRADITIONAL BENEFITS & ENERGETIC MEANING */}
      <section id="benefits" className="py-16 md:py-20 border-b border-orange-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200/60 text-primary text-xs font-bold uppercase tracking-wider mb-3">
              <IoSparkles /> Traditional Spiritual Significance
            </div>
            <h2 className="font-serif text-3xl font-bold text-accent sm:text-4xl">
              Aghori Evil Eye Locket Benefits &amp; Meaning
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-text-secondary sm:text-base">
              Rooted in ancient Eastern and Vedic tradition, this talisman serves as a protective emblem and mindfulness reminder for daily wear.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-3xl border border-orange-100 bg-[#FFFDF9] p-6 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center text-2xl mb-4">
                <IoEyeOutline />
              </div>
              <h3 className="font-serif text-lg font-bold text-accent mb-2">
                Drishti &amp; Nazar Absorption
              </h3>
              <p className="text-sm leading-relaxed text-text-secondary">
                Traditionally believed to shield the bearer by deflecting jealous glances, negative psychic projections, and subconscious friction in competitive social or workplace spaces.
              </p>
            </div>

            <div className="rounded-3xl border border-orange-100 bg-[#FFFDF9] p-6 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-stone-100 text-stone-800 flex items-center justify-center text-2xl mb-4">
                <IoShieldCheckmarkOutline />
              </div>
              <h3 className="font-serif text-lg font-bold text-accent mb-2">
                Natural Hakik Grounding
              </h3>
              <p className="text-sm leading-relaxed text-text-secondary">
                Original Hakik (natural Agate) is traditionally associated with root chakra grounding. Its soothing mineral weight helps dispel emotional restlessness and promotes centered inner calm.
              </p>
            </div>

            <div className="rounded-3xl border border-orange-100 bg-[#FFFDF9] p-6 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-orange-100 text-primary flex items-center justify-center text-2xl mb-4">
                <IoSparkles />
              </div>
              <h3 className="font-serif text-lg font-bold text-accent mb-2">
                Aghori Tradition Context
              </h3>
              <p className="text-sm leading-relaxed text-text-secondary">
                Named after the ancient Shaivite ascetic tradition that embodies fearlessness, equanimity, and the transcendence of duality, symbolizing energetic sovereignty and courage.
              </p>
            </div>

            <div className="rounded-3xl border border-orange-100 bg-[#FFFDF9] p-6 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-2xl mb-4">
                <IoCheckmarkCircle />
              </div>
              <h3 className="font-serif text-lg font-bold text-accent mb-2">
                Mindfulness Anchor
              </h3>
              <p className="text-sm leading-relaxed text-text-secondary">
                Worn as a tactile talisman, touching the locket during moments of stress or agitation serves as an instant physical cue to re-center attention and avoid reactive arguments.
              </p>
            </div>
          </div>

          {/* ETHICAL DISCLAIMER */}
          <div className="mt-8 rounded-2xl border border-orange-100 bg-orange-50/60 p-4 text-xs text-stone-600 leading-relaxed">
            <p className="font-bold text-accent mb-1 flex items-center gap-1">
              <IoInformationCircleOutline size={16} className="text-primary" /> Cultural &amp; Traditional Use Notice:
            </p>
            The Aghori Evil Eye Locket is intended for personal, cultural, and spiritual wear. Beliefs regarding negative energy absorption and talismanic protection are rooted in time-honored traditional practices and should not be construed as medical cures, psychological substitutes, or guaranteed worldly outcomes.
          </div>
        </div>
      </section>

      {/* 3. CRAFTSMANSHIP & CASING TRANSPARENCY */}
      <section id="craftsmanship" className="py-16 md:py-20 border-b border-orange-100 bg-[#FFFDF9]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200/60 text-primary text-xs font-bold uppercase tracking-wider mb-3">
                <IoInformationCircleOutline /> Mineral Authenticity &amp; Design
              </div>
              <h2 className="font-serif text-3xl font-bold text-accent sm:text-4xl leading-tight">
                Authentic Hakik Stone &amp; German Silver Engineering
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-text-secondary sm:text-base">
                We believe in total transparency. Every Aghori Locket is crafted from authentic mined stone rather than synthetic molded glass, bringing unique natural characteristics.
              </p>

              <div className="mt-6 space-y-4">
                <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                  <h3 className="font-bold text-accent text-base mb-1">
                    Natural Mine Cuts Are Proof of Authenticity
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    You may notice natural cuts, microscopic cleavage lines, or uneven facets on the stone. In gemology, these are known as mine cuts. They are natural mineral formations confirming the stone was cut from genuine earth-mined agate, not artificial plastic or uniform glass.
                  </p>
                </div>

                <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                  <h3 className="font-bold text-accent text-base mb-1">
                    Why the German Silver Casing Is Not Over-Tightened
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    The casing is intentionally given a normal, protective fit. A rigid, overtightened metallic frame places mechanical stress on natural agate, risking stone fracture during temperature shifts. Our balanced setting ensures long-term stone integrity.
                  </p>
                </div>

                <div className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5">
                  <h3 className="font-bold text-accent text-sm mb-1">
                    What if the Casing Comes Loose Over Time?
                  </h3>
                  <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
                    If the casing ever loosens after extensive daily wear, you can reattach it easily with a single tiny drop of Feviquick or jewelry adhesive. Alternatively, you may wrap the energized stone in a clean red cotton cloth and keep it in your pocket, purse, or home altar.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="overflow-hidden rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
                <h3 className="font-serif text-xl font-bold text-accent mb-4">
                  How to Wear &amp; Care for Your Locket
                </h3>
                <div className="space-y-3.5 text-sm">
                  <div className="flex items-start gap-3">
                    <IoTimeOutline className="text-primary mt-1 flex-shrink-0" size={18} />
                    <div>
                      <span className="font-bold text-accent">Recommended Start Day:</span>
                      <p className="text-text-secondary text-xs sm:text-sm mt-0.5">
                        Saturday is recommended. You may begin wearing it at any time during the day on Saturday.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <IoSparkles className="text-primary mt-1 flex-shrink-0" size={18} />
                    <div>
                      <span className="font-bold text-accent">Thread or Chain Options:</span>
                      <p className="text-text-secondary text-xs sm:text-sm mt-0.5">
                        Best worn with black cotton/silk thread or on a silver chain. Can be worn around your neck or waist.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <IoShieldCheckmarkOutline className="text-primary mt-1 flex-shrink-0" size={18} />
                    <div>
                      <span className="font-bold text-accent">Can It Be Worn Continuously?</span>
                      <p className="text-text-secondary text-xs sm:text-sm mt-0.5">
                        Yes. It is safe for continuous daily wear, including sleep, travel, and during menstruation. No removal is required.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <IoCheckmarkCircle className="text-primary mt-1 flex-shrink-0" size={18} />
                    <div>
                      <span className="font-bold text-accent">No Rituals Needed:</span>
                      <p className="text-text-secondary text-xs sm:text-sm mt-0.5">
                        Arrives consecrated. Simply unpack and start wearing it on Saturday with a clear positive intention.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECONDARY CONSULTATION BRIDGE */}
              <div className="rounded-3xl border border-orange-100 bg-[#FFF7E8] p-6 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wider text-primary">Need Personalized Guidance?</p>
                <h3 className="font-serif text-lg font-bold text-accent mt-1">Unsure Which Remedy Aligns With Your Kundli?</h3>
                <p className="mt-2 text-xs sm:text-sm text-text-secondary leading-relaxed">
                  While the Aghori Locket is universal, birth chart planetary afflictions (such as Shani, Rahu, or Mangal) may require specific astrological gemstone or Rudraksha alignment.
                </p>
                <Link
                  href="/consultations"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-accent hover:text-primary transition underline underline-offset-4"
                >
                  Consult an AstroJi Vedic Astrologer <IoArrowForward size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. REAL VERIFIED CUSTOMER REVIEWS */}
      <ReviewsCarousel
        reviews={reviews}
        averageRating={product.rating || 4.45}
        totalReviews={product.review_count || reviews.length}
        title="Verified Buyer Reviews"
        badgeText="Customer Feedback"
        containerId="customer-reviews"
        variant="light"
      />

      {/* 5. FREQUENTLY ASKED QUESTIONS */}
      <section id="faqs" className="py-16 md:py-20 border-b border-orange-100 bg-[#FFFDF9]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200/60 text-primary text-xs font-bold uppercase tracking-wider mb-2">
              <IoHelpCircleOutline /> Clarifications
            </div>
            <h2 className="font-serif text-3xl font-bold text-accent sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-2 text-sm text-text-secondary">
              Everything you need to know about the Aghori Locket stone, wearing rules, and daily care.
            </p>
          </div>

          <div className="space-y-3.5">
            {faqs.map((faq, index) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-orange-100 bg-white p-5 shadow-sm transition hover:border-amber-300 [&_summary::-webkit-details-marker]:hidden"
                {...(index === 0 ? { open: true } : {})}
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-accent text-sm sm:text-base gap-3">
                  <span>{faq.question}</span>
                  <span className="w-6 h-6 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0 text-primary group-open:rotate-180 transition-transform text-xs">
                    ▼
                  </span>
                </summary>
                <p className="mt-3 text-xs sm:text-sm leading-relaxed text-text-secondary border-t border-orange-50 pt-3">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 6. RELATED EVIL EYE COLLECTION */}
      {relatedProducts && relatedProducts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-primary">Related Spiritual Amulets</p>
                <h2 className="font-serif text-2xl font-bold text-accent">More from the Evil Eye Collection</h2>
              </div>
              <Link
                href="/products?byTag=evil-eye"
                className="text-xs font-bold text-primary underline underline-offset-4 hover:text-amber-700"
              >
                View Full Collection →
              </Link>
            </div>
            <ProductGrid products={relatedProducts} loading={false} variant="carousel" />
          </div>
        </section>
      )}

      {/* 7. MOBILE STICKY PURCHASE BAR */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 border-t border-orange-100 bg-white/95 px-4 py-3 backdrop-blur-md transition-transform duration-300 lg:hidden ${
          isStickyVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {images[0] && (
              <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg border border-orange-100">
                <Image src={images[0]} alt="Aghori Locket" fill className="object-cover" />
              </div>
            )}
            <div>
              <p className="text-xs font-bold text-accent truncate max-w-[150px]">Aghori Evil Eye Locket</p>
              <p className="font-serif text-sm font-bold text-primary">₹{product.price}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleBuyNow}
            className="rounded-full bg-accent px-5 py-2 text-xs font-bold text-white shadow hover:bg-accent/90 transition"
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
}
